import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { defaultTo, path } from 'ramda';
import { withFormik } from 'formik';
import * as yup from 'yup';

import { apiContactProfessional } from '../../api/user';
import { isNilOrEmpty } from '../../util/redux';
import { handleError } from '../../util/forms';

import Modal from '../Modal';
import EditField from '../Forms/EditField';
import Checkbox from '../Forms/Checkbox';

class ContactModal extends Component {
  static propTypes = {
    user: PropTypes.object,
    onHide: PropTypes.func,
    onContacted: PropTypes.func,
  };

  state = {
    show: false,
    success: null,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState && !prevState.show && nextProps.show && nextProps.resetForm) {
      nextProps.resetForm();
      return { show: true, success: null };
    }

    return { show: defaultTo(false, nextProps.show) };
  }

  render() {
    const {
      onHide,
      values,
      errors,
      handleBlur,
      handleChange,
      handleSubmit,
      isSubmitting,
      isValid,
      status,
    } = this.props;
    const { show } = this.state;
    const success = path(['success'], status);
    return (
      <Modal show={show} onHide={() => onHide()} title="Contact Form">
        <form>
          <Modal.Body>
            {success === true ? (
              <h3>Your message has been sent.</h3>
            ) : success === false ? (
              <h3>There was a problem sending your message, please try again later.</h3>
            ) : (
              <Fragment>
                <p>
                  When completing this form, please include all information and a message regarding the financial
                  counseling, coaching, or education services you are seeking. Once submitted, the Sage Financial Solutions professional
                  will be notified of your request.
                </p>
                <div className="row">
                  <div className="col-md-6">
                    <EditField
                      block
                      name="prefix"
                      placeholder="Prefix"
                      error={errors.prefix}
                      status={status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.prefix}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <EditField
                      block
                      name="name"
                      placeholder="Name"
                      error={errors.name}
                      status={status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <EditField
                      block
                      name="city"
                      placeholder="City"
                      error={errors.city}
                      status={status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.city}
                    />
                  </div>
                  <div className="col-md-6">
                    <EditField
                      block
                      name="state"
                      placeholder="State"
                      error={errors.state}
                      status={status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.state}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <EditField
                      block
                      name="email"
                      placeholder="Email"
                      status={status}
                      error={errors.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                    />
                  </div>
                  <div className="col-md-6">
                    <EditField
                      block
                      name="phone"
                      placeholder="Phone"
                      status={status}
                      error={errors.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phone}
                    />
                  </div>
                </div>
                <EditField
                  as="textarea"
                  block
                  name="message"
                  placeholder="Message"
                  status={status}
                  error={errors.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.message}
                />
                <p className="tip">
                  Sage Financial Solutions® expressly prohibits use of these services to market unsolicited products or services to Sage Financial Solutions®
                  certified professionals. Sage Financial Solutions' "Find a Certified Professional" search tool and this "Contact" form
                  are intended strictly as services that allow consumers to contact an Sage Financial Solutions certified professional for
                  the purpose of obtaining financial advice. Sage Financial Solutions does not recommend or endorse individual
                  professionals or professional services. By clicking "Send," I acknowledge these terms of use and
                  authorize Sage Financial Solutions to provide the information entered above to the certified professional identified when
                  completing this form.
                </p>
                <Checkbox
                  name="agree"
                  label="I agree"
                  checked={values.agree}
                  error={errors.agree}
                  onChange={handleChange}
                />
              </Fragment>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={() => onHide()}>
              Close
            </button>
            {isNilOrEmpty(success) && (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit}
              >
                Send
              </button>
            )}
          </Modal.Footer>
        </form>
      </Modal>
    );
  }
}

export default withFormik({
  handleSubmit: (payload, formProps) => {
    const { props, setStatus } = formProps;

    apiContactProfessional(props.user.id, payload)
      .then(() => setStatus({ success: true }))
      .catch((err) => handleError(formProps, err));
  },
  mapPropsToValues: () => ({
    prefix: '',
    name: '',
    city: '',
    state: '',
    email: '',
    phone: '',
    message: '',
    agree: false,
  }),
  validationSchema: () =>
    yup.object().shape({
      name: yup.string().required('Required'),
      city: yup.string().required('Required'),
      state: yup.string().required('Required'),
      email: yup.string().required('Required'),
      message: yup.string().required('Required'),
      agree: yup.boolean().equals([true], 'You must agree to the terms above'),
    }),
  isInitialValid: ({ initialValues, validationSchema }) => validationSchema().isValidSync(initialValues),
  validateOnChange: true,
})(ContactModal);
